import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Truck, DollarSign, PhoneCall, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
const features = [{
  icon: Truck,
  title: 'Local Trucks, Faster Delivery'
}, {
  icon: DollarSign,
  title: 'Direct From the Supplier'
}, {
  icon: PhoneCall,
  title: 'Real People on the Phone'
}, {
  icon: Mountain,
  title: 'Knows the Northwest'
}];
export function WhyUs() {
  const t = useT();
  return <section className="py-10 md:py-14 dark:bg-[#474443] transition-colors duration-300 border-y border-stone-200 dark:border-[#3f3d3c] bg-[#FCF9F2] relative overflow-hidden" id="why-us" data-id="element-1737">
      {/* Subtle decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-orange/40 to-transparent" data-id="element-1738" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1739">
        {/* TOP BAND — Headline left, supporting copy + CTA right */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-end mb-8 lg:mb-10" data-id="element-1740">
          <div className="lg:col-span-6" data-id="element-1741">
            <div className="inline-flex items-center gap-2 mb-3" data-id="element-1742">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-1743" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-1744">
                {t('why.localOwned')}
              </span>
            </div>

            <h2 className="md:text-5xl lg:text-6xl xl:text-[4rem] font-heading font-extrabold text-stone-900 dark:text-white tracking-tight text-[50px]" data-id="element-1745">
              We're the dispatcher.{' '}
              <span className="text-stone-500 dark:text-stone-400" data-id="element-1746">
                We're the driver. We're local.
              </span>
            </h2>
          </div>

          <div className="lg:col-span-6 lg:pl-6 lg:border-l lg:border-stone-200 dark:lg:border-white/10" data-id="element-1747">
            <p className="text-stone-600 dark:text-stone-300 md:text-lg font-medium leading-relaxed mb-4" data-id="element-1748">
              {t('why.localBody')}
            </p>
            <Link to="/local-vs-national" className="group inline-flex items-center gap-2 text-brand-orange hover:text-brand-orange/80 font-bold text-sm tracking-tight transition-colors" data-id="element-1749">
              <span className="border-b border-brand-orange/30 group-hover:border-brand-orange pb-0.5 transition-colors" data-id="element-1750">
                See how we stack up vs. national marketplaces
              </span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 shrink-0" data-id="element-1751" />
            </Link>
          </div>
        </motion.div>

        {/* BOTTOM BAND — 4 features full width */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 pt-6 border-t border-stone-200 dark:border-white/10" data-id="element-1752">
          {features.map((feature, index) => <motion.div key={feature.title} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          delay: index * 0.08,
          duration: 0.4,
          ease: 'easeOut'
        }} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-white/60 dark:bg-white/[0.03] border border-stone-200/70 dark:border-white/[0.08]" data-id="element-1753">
              <div className="w-10 h-10 bg-brand-dim/10 dark:bg-brand-dim/20 rounded-lg flex items-center justify-center border border-brand-dim/20 dark:border-brand-dim/30 shrink-0" data-id="element-1754">
                <feature.icon className="w-5 h-5 text-brand-dim" data-id="element-1755" />
              </div>
              <h3 className="font-heading text-sm sm:text-base font-bold text-brand-charcoal dark:text-white leading-snug" data-id="element-1756">
                {feature.title}
              </h3>
            </motion.div>)}
        </div>
      </div>
    </section>;
}